import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect } from 'react';
export const HourlyRateConfig = ({ hourlyRate, onRateChange, disabled = false }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [tempRate, setTempRate] = useState(hourlyRate.toString());
    const [isValid, setIsValid] = useState(true);
    const inputRef = useRef(null);
    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);
    const validateRate = (value) => {
        const numValue = parseFloat(value);
        return !isNaN(numValue) && numValue > 0 && numValue <= 1000;
    };
    const handleEdit = () => {
        if (disabled)
            return;
        setTempRate(hourlyRate.toString());
        setIsEditing(true);
        setIsValid(true);
    };
    const handleSave = () => {
        if (validateRate(tempRate)) {
            const newRate = parseFloat(tempRate);
            onRateChange(newRate);
            setIsEditing(false);
            setIsValid(true);
        }
        else {
            setIsValid(false);
        }
    };
    const handleCancel = () => {
        setIsEditing(false);
        setTempRate(hourlyRate.toString());
        setIsValid(true);
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSave();
        }
        else if (e.key === 'Escape') {
            handleCancel();
        }
    };
    const handleInputChange = (e) => {
        const value = e.target.value;
        setTempRate(value);
        setIsValid(validateRate(value));
    };
    if (isEditing) {
        return (_jsx("div", { className: "inline-flex items-center", children: _jsxs("div", { className: "flex items-center space-x-1 px-2 py-1 rounded-full bg-blue-50 border border-blue-200", children: [_jsx("svg", { className: "w-3 h-3 text-blue-600", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" }) }), _jsx("input", { ref: inputRef, type: "number", value: tempRate, onChange: handleInputChange, onKeyDown: handleKeyDown, onBlur: handleSave, className: `w-12 text-xs font-medium bg-transparent border-none outline-none text-center ${isValid ? 'text-blue-700' : 'text-red-600'}`, placeholder: "50", min: "1", max: "1000", step: "5" }), _jsx("span", { className: "text-xs text-blue-600", children: "/hr" })] }) }));
    }
    return (_jsxs("button", { onClick: handleEdit, disabled: disabled, className: `
        inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium
        transition-all duration-200 hover:shadow-sm
        ${disabled
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-slate-100 text-slate-700 hover:bg-slate-200 cursor-pointer'}
      `, title: "Click to edit your hourly rate for personalized savings calculations", children: [_jsx("svg", { className: "w-3 h-3", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" }) }), _jsxs("span", { children: ["\uD83D\uDCBC $", hourlyRate, "/hr"] }), _jsx("svg", { className: "w-3 h-3 opacity-60", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" }) })] }));
};
// Common hourly rate presets
export const HOURLY_RATE_PRESETS = [
    { label: 'Student', value: 25 },
    { label: 'Professional', value: 75 },
    { label: 'Senior', value: 125 },
    { label: 'Executive', value: 200 }
];
export const HourlyRatePresetSelector = ({ currentRate, onRateSelect, onClose }) => {
    return (_jsxs("div", { className: "absolute top-8 left-0 bg-white rounded-lg shadow-lg border border-gray-200 p-2 z-50", children: [_jsx("div", { className: "text-xs text-gray-500 mb-2 px-2", children: "Quick presets:" }), _jsx("div", { className: "space-y-1", children: HOURLY_RATE_PRESETS.map((preset) => (_jsxs("button", { onClick: () => onRateSelect(preset.value), className: `
              w-full text-left px-3 py-1.5 rounded text-xs hover:bg-gray-50
              ${currentRate === preset.value ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}
            `, children: [preset.label, " - $", preset.value, "/hr"] }, preset.value))) }), _jsx("div", { className: "border-t mt-2 pt-2", children: _jsx("button", { onClick: onClose, className: "w-full text-xs text-gray-500 hover:text-gray-700 py-1", children: "Cancel" }) })] }));
};
